import { useEffect, useState, useMemo } from 'react';

import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash';

import { ReactComponent as AddIcon } from 'assets/images/icons/btn_add.svg';
import { ReactComponent as AddIconDisabled } from 'assets/images/icons/btn_add_disabled.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';
import { ReactComponent as ImportIcon } from 'assets/images/icons/icn_table_cell_download.svg';
import { ReactComponent as ImportIconDisabled } from 'assets/images/icons/icn_table_cell_download_grayed.svg';

import { Box, TextField, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Hooks
import useAuthScope from 'hooks/useAuthScope';
import useDataSourceManager from 'hooks/useDataSourceManager';

// Components
import { Loading } from 'components/Loading';
import GlobalAlert from 'components/Notification';
import { MenuSelect } from 'components/Select/menuSelect';
import { RadioButton } from 'components/buttons/RadioButton';
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';
import { OutlineRectangleButton } from 'components/buttons/OutlineRectangleButton';

// Const
import { voiceVlanConfig, MAX_OUI_BINDING_ENTRIES } from './const';

import AddModal from './components/AddModal';
import ImportModal from './components/ImportModal';
import OuiBindingTable from './components/OuiBindingTable';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const validateMacAddress = macAddress => {
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
  return macRegex.test(macAddress);
};

const VoiceVlan = ({ showEditGeneral }) => {
  const theme = useTheme();

  const { auth, standalone, isAuthReady } = useAuthScope();
  const { dataMap, getData, sendBatch, sendRequest } = useDataSourceManager();

  const [readVoiceVlanUuid, _setReadVoiceVlanUuid] = useState(uuidv4());
  const [updateVoiceVlanUuid, _setUpdateVoiceVlanUuid] = useState(uuidv4());

  const [formData, setFormData] = useState(cloneDeep(voiceVlanConfig));
  const [defaultFormData, setDefaultFormData] = useState(cloneDeep(voiceVlanConfig));
  const [originalFormData, setOriginalFormData] = useState(null); // API response data

  const [hasChecked, setHasChecked] = useState(false);
  const [isSelectedCount, setIsSelectedCount] = useState(0);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  };

  const labelStyle = {
    width: '200px',
    minWidth: '200px',
    color: theme.palette.primary.black,
    fontSize: theme.typography.customFontSize[2],
    display: 'flex',
    alignItems: 'center',
    height: '40px',
  };

  const textfieldStyle = {
    width: '300px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.customBorderRadius[1],
      height: '30px',
      '& fieldset': {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.mainHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    // Hide native number spinners (WebKit)
    '& .MuiOutlinedInput-root input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-root input[type="number"]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    // Firefox / generic appearance
    '& .MuiOutlinedInput-root input[type="number"]': {
      MozAppearance: 'textfield',
      appearance: 'textfield',
    },
  };

  const inputHasError = useMemo(() => {
    const list = formData?.ouiBindingList || [];
    if (list.length === 0) return false;

    // normalize addresses/masks for comparison (lowercase, '-' -> ':')
    const normalized = list.map(item => {
      const address = (item.address || '').toLowerCase().replace(/-/g, ':');
      const mask = (item.mask || '').toLowerCase().replace(/-/g, ':');
      return { ...item, address, mask };
    });

    // 1) empty field or placeholder
    const hasEmpty = normalized.some(i => !i.address || !i.mask || i.mask === '-----');
    if (hasEmpty) return true;

    // 2) invalid MAC format
    const hasInvalid = normalized.some(i => !validateMacAddress(i.address) || !validateMacAddress(i.mask));
    if (hasInvalid) return true;

    // 3) duplicate addresses (normalized)
    const counts = normalized.reduce((acc, i) => {
      acc[i.address] = (acc[i.address] || 0) + 1;
      return acc;
    }, {});
    const hasDuplicate = Object.values(counts).some(c => c > 1);
    if (hasDuplicate) return true;

    return false;
  }, [formData?.ouiBindingList]);

  const handleChangeItem = (list, newValue) => {
    const newFormData = cloneDeep(formData);
    if (newFormData?.[list]?.length > 0) {
      newFormData[list] = newFormData?.[list]?.map(item => ({
        ...item,
        isActive: item.value === newValue,
      }));
    }
    setFormData(newFormData);
  };

  const handleClickDeleteBtn = () => {
    if (!hasChecked) return;

    const newOuiBindingList = formData?.ouiBindingList?.filter(item => !item.checked);
    setFormData({
      ...formData,
      ouiBindingList: newOuiBindingList,
    });
  };

  const isSaveDisabled = useMemo(() => {
    if (!showEditGeneral) return true;
    if (formData?.voiceVlanOption === 'DISABLE') return false;

    const vlanId = formData?.voiceVlanId;
    const ouiListLength = formData?.ouiBindingList?.length ?? 0;

    const invalidVlan = vlanId < 2 || vlanId > 4094;
    const invalidOuiList = ouiListLength === 0 || ouiListLength > MAX_OUI_BINDING_ENTRIES;

    return invalidVlan || invalidOuiList || inputHasError;
  }, [showEditGeneral, formData?.voiceVlanOption, formData?.voiceVlanId, formData?.ouiBindingList?.length, inputHasError]);

  const handleClickSaveBtn = () => {
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/wiredVoiceVlan/:id.update`,
      site: `${baseUrl}/sites/:siteId/settings/wiredVoiceVlan/:id.update`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
        id: originalFormData?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
        id: originalFormData?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    let payload = cloneDeep(originalFormData);

    if (formData?.voiceVlanOption === 'ENABLE') {
      // Helper: normalize MAC separators from '-' to ':' before sending to backend
      // Example: convert 00-11-22-33-44-55 -> 00:11:22:33:44:55
      const formatMac = str => (typeof str === 'string' ? str.replace(/-/g, ':') : str);

      const formattedOuiBindingList = (formData?.ouiBindingList || []).map(({ checked, address, mask, ...rest }) => ({
        ...rest,
        address: formatMac(address),
        mask: formatMac(mask),
      }));

      payload = {
        ...payload,
        enable: true,
        voiceVlanId: Number(formData?.voiceVlanId) ?? 2,
        voiceVlanCos: Number(formData?.voiceVlanCosList?.find(item => item.isActive)?.value) ?? 5,
        ouiBindingList: formattedOuiBindingList,
      };
    } else {
      payload = {
        ...payload,
        enable: false,
        voiceVlanId: null,
        voiceVlanCos: null,
        ouiBindingList: [],
      };
    }

    try {
      sendRequest(apiKey, updateVoiceVlanUuid, restfulParams, payload);
    } catch (err) {
      setIsLoading(false);
      setAlertInfo({
        open: true,
        severity: 'error',
        message: err?.message || 'Failed to send update request',
      });
    }
  };

  const handleClickCancelBtn = () => {
    // Reset the formData to initial state
    setFormData(defaultFormData);
  };

  const parseVoiceVlanInfo = data => {
    const { enable, voiceVlanId, voiceVlanCos, ouiBindingList } = data;
    const newFormData = cloneDeep(voiceVlanConfig);

    if (enable) {
      newFormData.voiceVlanOption = enable ? 'ENABLE' : 'DISABLE';
      newFormData.voiceVlanId = voiceVlanId ?? 2;
      newFormData.voiceVlanCosList = newFormData?.voiceVlanCosList?.map(item => ({
        ...item,
        isActive: Number(item.value) === Number(voiceVlanCos),
      }));

      if (ouiBindingList?.length > 0) {
        // Format MACs for UI display (convert ':' -> '-') to match input mask / display style
        const formatMac = str => (typeof str === 'string' ? str.replace(/:/g, '-') : str);

        const formattedOuiBindingList = (ouiBindingList || []).map(item => ({
          ...item,
          address: formatMac(item.address),
          mask: formatMac(item.mask),
        }));

        newFormData.ouiBindingList = formattedOuiBindingList;
      } else {
        newFormData.ouiBindingList = [];
      }
    }

    setFormData(newFormData);
    setDefaultFormData(newFormData);
  };

  const fetchVoiceVlan = () => {
    const apiUrl = standalone
      ? `/orgs/:orgId/sites/:siteId/settings/wiredVoiceVlan.read`
      : `/ents/:entId/orgs/:orgId/sites/:siteId/settings/wiredVoiceVlan.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      restfulPlaceholders: restfulParams,
      params: {},
    };
  };

  const init = async () => {
    setIsLoading(true);

    try {
      const voiceVlanParams = fetchVoiceVlan();

      const { getSendBatchData } = await sendBatch([{ ...voiceVlanParams, requestUuid: readVoiceVlanUuid }]);

      const voiceVlanRes = getSendBatchData({ requestUuid: readVoiceVlanUuid });

      if (voiceVlanRes?.data) {
        setOriginalFormData(cloneDeep(voiceVlanRes?.data));
        parseVoiceVlanInfo(voiceVlanRes?.data);
      } else {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: voiceVlanRes?.message || 'Error',
        });
        setFormData(cloneDeep(voiceVlanConfig));
      }
    } catch (err) {
      setAlertInfo({
        open: true,
        severity: 'error',
        message: err?.message || 'Failed to fetch Voice VLAN',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isUpdateResponse = getData(updateVoiceVlanUuid);
    if (isUpdateResponse) {
      if (checkMockup) {
        if (isUpdateResponse && isUpdateResponse?.data) {
          setIsLoading(false);
          // init();
        }
      } else {
        if (isUpdateResponse.hasOwnProperty('code') && isUpdateResponse?.code !== 200) {
          setIsLoading(false);
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isUpdateResponse?.message,
          });
        } else {
          init();
        }
      }
    } else {
      setIsLoading(false);
    }
  }, [dataMap]);

  useEffect(() => {
    if (!isAuthReady) return;
    init();
  }, [isAuthReady]);

  return (
    <>
      {isLoading && <Loading />}

      <Box sx={{ padding: '20px 30px' }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[1],
          }}
        >
          Voice VLAN
        </Typography>

        <Divider sx={{ margin: '20px 0' }} />

        <Box sx={rowStyle}>
          <Box sx={{ ...labelStyle }}>Status</Box>
          <Box>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ width: 100 }}>
                <RadioButton
                  label="Enable"
                  name="voiceVlanOption"
                  value={'ENABLE'}
                  checked={formData?.voiceVlanOption === 'ENABLE'}
                  onChange={() => setFormData(prev => ({ ...prev, voiceVlanOption: 'ENABLE' }))}
                  disabled={!showEditGeneral}
                />
              </Box>
              <Box sx={{ width: 100 }}>
                <RadioButton
                  label="Disable"
                  name="voiceVlanOption"
                  value={'DISABLE'}
                  checked={formData?.voiceVlanOption === 'DISABLE'}
                  onChange={() => setFormData(prev => ({ ...prev, voiceVlanOption: 'DISABLE' }))}
                  disabled={!showEditGeneral}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {formData?.voiceVlanOption === 'ENABLE' && (
          <Box>
            {/* Voice VLAN ID */}
            <Box sx={{ ...rowStyle, marginBottom: '10px' }}>
              <Box sx={{ ...labelStyle, alignItems: 'flex-start', paddingTop: formData?.voiceVlanId < 2 || formData?.voiceVlanId > 4094 ? '0px' : '11px' }}>Voice VLAN ID</Box>
              <Box sx={{ marginTop: '5px', display: 'inline-flex', alignItems: 'flex-start' }}>
                <TextField
                  sx={textfieldStyle}
                  InputProps={{
                    sx: {
                      height: '29px',
                      display: 'flex',
                      alignItems: 'center',
                      '& input': {
                        padding: '6px 8px',
                        height: '100%',
                        boxSizing: 'border-box',
                      },
                    },
                  }}
                  autoComplete="off"
                  type="number"
                  min="2"
                  max="4094"
                  value={formData?.voiceVlanId ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, voiceVlanId: e.target.value === '' ? '' : Number(e.target.value) }))}
                  error={formData?.voiceVlanId < 2 || formData?.voiceVlanId > 4094}
                  helperText={formData?.voiceVlanId < 2 || formData?.voiceVlanId > 4094 ? 'A valid value for vlan is from 2~4094' : ''}
                  disabled={!showEditGeneral}
                />
                <Typography sx={{ paddingTop: '6px', marginLeft: '10px', fontSize: '13px', color: theme.palette.primary.greySecound }}>(2~4094)</Typography>
              </Box>
            </Box>
            {/* Voice VLAN CoS */}
            <Box sx={{ ...rowStyle, marginBottom: '10px' }}>
              <Box sx={{ ...labelStyle }}>Voice VLAN CoS</Box>
              <Box sx={{ marginTop: 0, height: '29px', display: 'flex', alignItems: 'center' }}>
                <MenuSelect
                  width={textfieldStyle.width}
                  value={formData?.voiceVlanCosList?.find(item => item.isActive)?.value ?? ''}
                  options={formData?.voiceVlanCosList}
                  onChange={obj => handleChangeItem('voiceVlanCosList', obj.value)}
                  disabled={!showEditGeneral}
                />
              </Box>
            </Box>
            {/* OUI Binding List (0/10) */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle }}>
                OUI Binding List
                <Box sx={{ marginLeft: '4px', fontWeight: theme.typography.customFontWeight[1] }}>({formData?.ouiBindingList?.length || 0}/10)</Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex' }}>
                  {/* Add button */}
                  <OutlineRectangleButton
                    startIcon={!showEditGeneral || (formData?.ouiBindingList?.length || 0) >= MAX_OUI_BINDING_ENTRIES ? <AddIconDisabled /> : <AddIcon />}
                    width="80px"
                    height="30px"
                    onClick={() => setOpenAddModal(true)}
                    disabled={!showEditGeneral || (formData?.ouiBindingList?.length || 0) >= MAX_OUI_BINDING_ENTRIES}
                  >
                    Add
                  </OutlineRectangleButton>
                  {/* Delete button */}
                  {isSelectedCount > 0 && (
                    <OutlineRectangleButton sx={{ marginLeft: '10px' }} startIcon={<DeleteIcon />} height="30px" onClick={handleClickDeleteBtn}>
                      {`Delete `}
                      <Box
                        sx={{
                          width: '20.2px',
                          height: '20.8px',
                          textAlign: 'center',
                          marginLeft: '4px',
                          borderRadius: '10px',
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.white,
                        }}
                      >
                        {isSelectedCount}
                      </Box>
                    </OutlineRectangleButton>
                  )}
                </Box>
                {/* Import button */}
                <OutlineRectangleButton
                  startIcon={!showEditGeneral || (formData?.ouiBindingList?.length || 0) >= MAX_OUI_BINDING_ENTRIES ? <ImportIconDisabled /> : <ImportIcon />}
                  width="200px"
                  height="30px"
                  onClick={() => setOpenImportModal(true)}
                  disabled={!showEditGeneral || (formData?.ouiBindingList?.length || 0) >= MAX_OUI_BINDING_ENTRIES}
                >
                  Import Predefined OUI
                </OutlineRectangleButton>
              </Box>
            </Box>
            {/* OUI Binding Table */}
            <Box>
              <OuiBindingTable
                {...{
                  formData,
                  setFormData,
                  hasChecked,
                  setHasChecked,
                  setIsSelectedCount,
                  showEditGeneral,
                }}
              />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            marginTop: '50px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <OutlineButton sx={{ marginRight: '20px' }} width="100px" height="32px" onClick={handleClickCancelBtn} disabled={!showEditGeneral}>
            Cancel
          </OutlineButton>
          <SolidButton width="100px" height="32px" onClick={handleClickSaveBtn} disabled={isSaveDisabled}>
            Save
          </SolidButton>
        </Box>

        <AddModal
          {...{
            openAddModal,
            setOpenAddModal,
            formData,
            setFormData,
            showEditGeneral,
          }}
        />

        <ImportModal
          {...{
            openImportModal,
            setOpenImportModal,
            showEditGeneral,
            formData,
            setFormData,
          }}
        />

        <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
      </Box>
    </>
  );
};

export default VoiceVlan;
